/**
 * フロントエンド設定管理サービス
 * バックエンドAPIから環境変数を取得し、アプリ全体で利用可能にする
 */

class ConfigService {
  constructor() {
    this.config = null;
    this.loading = false;
    this.error = null;
  }

  /**
   * CSRFトークンを取得
   * @private
   * @returns {Promise<string|null>}
   */
  async fetchCsrfToken() {
    try {
      const response = await fetch('/api/csrf/csrf-token', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error('[CONFIG SERVICE] Failed to fetch CSRF token:', response.status);
        return null;
      }

      const data = await response.json();
      
      // 様々なレスポンス形式に対応
      if (typeof data === 'string') {
        return data;
      } else if (data && typeof data === 'object') {
        return data.token || data.csrfToken || data._csrf || null;
      }
      
      return null;
    } catch (error) {
      console.error('[CONFIG SERVICE] Error fetching CSRF token:', error);
      return null;
    }
  }

  /**
   * バックエンドから環境変数を取得
   * @returns {Promise<Object>} 設定オブジェクト
   */
  async fetchConfig() {
    if (this.loading) {
      return this.config;
    }

    if (this.config) {
      return this.config;
    }

    this.loading = true;
    this.error = null;

    try {
      // ① まずCSRFトークンを取得
      const csrfToken = await this.fetchCsrfToken();
      
      if (!csrfToken) {
        throw new Error('CSRFトークンの取得に失敗しました');
      }

      // 取得したいキーを指定
      const keys = [
        'paygentMerchantId',
        'paygentTokenKey',
        'paygentApiUrl',
        'postalCodeApiUrl',
        'allowedIps'
      ].join(',');

      const response = await fetch(`/api/public/config?keys=${keys}`, {
        method: 'GET',
        credentials: 'include', // Cookie送信
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Failed to fetch config: ${response.status} ${errorData.message || response.statusText}`);
      }

      this.config = await response.json();
      
      return this.config;
    } catch (error) {
      console.error('[CONFIG SERVICE] Error fetching config:', error);
      this.error = error;
      throw error;
    } finally {
      this.loading = false;
    }
  }

  /**
   * 設定値を取得(キャッシュから)
   * @param {string} key - 設定キー
   * @returns {string|null} 設定値
   */
  getConfig(key) {
    if (!this.config) {
      console.warn('[CONFIG SERVICE] Config not loaded yet. Call fetchConfig() first.');
      return null;
    }
    return this.config[key];
  }

  /**
   * 全設定を取得
   * @returns {Object|null} 全設定オブジェクト
   */
  getAllConfig() {
    return this.config;
  }

  /**
   * 設定が読み込まれているかチェック
   * @returns {boolean}
   */
  isLoaded() {
    return this.config !== null;
  }

  /**
   * ローディング中かチェック
   * @returns {boolean}
   */
  isLoading() {
    return this.loading;
  }

  /**
   * エラーを取得
   * @returns {Error|null}
   */
  getError() {
    return this.error;
  }

  /**
   * 設定をリセット(テスト用)
   */
  reset() {
    this.config = null;
    this.loading = false;
    this.error = null;
  }

  /**
   * 値をマスク表示(ログ用)
   * @private
   */
  maskValue(value) {
    if (!value || value.length === 0) {
      return '(empty)';
    }
    if (value.length <= 4) {
      return '****';
    }
    return value.substring(0, 4) + '****';
  }
}

// シングルトンインスタンスをエクスポート
export const configService = new ConfigService();